import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { createCsvUploadBeforeUploadHandler, getFileMetadata } from '../../../shared/utils/upload-file.util';
import { AuthService } from '../../../shared/services/auth.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { UploadFilesService } from '../../services/upload-files.service';
import {
  ExportStatus,
  FileMetadata,
  ProcessResponse,
  PublishResponse,
  TargetColumnsConfig,
  UploadStatus,
  ValidateFileResponse,
} from '../../../../API';
import { FileItem, MappingFormValue } from '../../models/files';
import type { FileValidationLog, ValidationLogsInput } from '../../models/validation-logs';
import { Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';

@Component({
  selector: 'app-first-time-file-upload',
  templateUrl: './first-time-file-upload.component.html',
  styleUrl: './first-time-file-upload.component.scss',
})
export class FirstTimeFileUploadComponent implements OnInit, OnDestroy {
  files: FileItem[] = [];
  form!: FormGroup;

  fileOptions: any[] = [];
  columnOptionsByFile: Record<string, string[]> = {};
  targetFields: TargetColumnsConfig[] = [];

  current = 0;
  index = 'uploadFile';
  validationLogs: ValidationLogsInput = '';

  spinning: boolean = false;
  progressRatio: number = 0;
  isLoading = false;
  isProcessing = false;
  isPublishing = false;
  isFileFormValid: boolean = false;
  isProcessed: boolean = false;
  filesValidated: boolean = false;

  destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private nzMessageService: NzMessageService,
    private authService: AuthService,
    private userManagementService: UserManagementService,
    public uploadFilesService: UploadFilesService,
    private router: Router,
  ) {
    this.addFile();
    this.targetFields = this.authService.getTargetColumns();
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      mappings: this.fb.array(this.targetFields.map((f) => this.createRow(f))),
    });

    this.uploadFilesService.listenRealtimeChanges();

    this.uploadFilesService.updatedItem$.pipe(takeUntil(this.destroy$)).subscribe((updatedItem) => {
      if (updatedItem) {
        this.updateItemInList(updatedItem);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearFiles();
  }

  checkFormValidity(isValid: boolean): void {
    this.isFileFormValid = isValid;
  }

  addFile() {
    this.files.push({
      fileForm: this.createFileForm(),
      headers: [],
      uploadedFileList: [],
    });
  }

  createFileForm(): FormGroup {
    return this.fb.group({
      fileName: new FormControl('', [Validators.required]),
      description: new FormControl('', [Validators.required]),
    });
  }

  get mappings(): FormArray {
    return this.form.get('mappings') as FormArray;
  }

  get hasFilesSelected(): boolean {
    return this.files.some((file) => file.uploadedFileList && file.uploadedFileList.length > 0);
  }

  async validateAndSaveMapping(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.validationLogs = '';

    try {
      const targetColumnMapping = this.buildTargetColumnMapping();
      const isMappingSaved = await this.updateTargetColumn(targetColumnMapping);

      if (!isMappingSaved) {
        return;
      }

      await this.validateUploadedFiles();
    } finally {
      this.isLoading = false;
    }
  }

  async updateTargetColumn(targetColumnMapping: Record<string, string>): Promise<boolean> {
    const orgId = this.userManagementService.getOrgId();
    const fileTemplateConfig = this.files.map((fileItem, index) => {
      const { fileName: name, description } = fileItem.fileForm.value;
      return {
        description: description,
        displayName: name,
        headers: JSON.stringify(fileItem.headers),
        templateId: `${orgId}_tpl_${index + 1}`,
      };
    });

    const payload: any = {
      targetColumnMapping: targetColumnMapping,
      fileTemplateConfig: fileTemplateConfig,
    };

    try {
      const response = await this.uploadFilesService.updateOrganization(payload);
      if (response) {
        return true;
      }
    } catch (error) {
      console.error(error);
      this.nzMessageService.error('Unable to map the columns.');
    }

    return false;
  }

  private buildTargetColumnMapping(): Record<string, string> {
    const formValue = this.form.value.mappings as MappingFormValue[];

    return formValue.reduce<Record<string, string>>((targetColumnMapping, item) => {
      if (item.key && item.column) {
        targetColumnMapping[item.key] = item.column;
      }

      return targetColumnMapping;
    }, {});
  }

  private async validateUploadedFiles(): Promise<void> {
    this.validationLogs = 'Validating file contents...';

    try {
      const validationResults: FileValidationLog[] = [];

      for (const fileItem of this.files) {
        const validationResult = await this.validateUploadedFile(fileItem);
        validationResults.push({ fileName: fileItem.nzFile?.name, result: validationResult });

        if (validationResult) {
          await this.persistValidationResult(fileItem, validationResult);
        }
      }

      this.validationLogs = validationResults;
      this.filesValidated = validationResults.length > 0 && validationResults.every(({ result }) => result.isValid);

      if (this.filesValidated) {
        this.nzMessageService.success('Column mapping saved and file validation completed.');
      } else {
        this.nzMessageService.warning('Column mapping saved and file validation completed with issues.');
      }
    } catch (error: unknown) {
      console.error(error);
      this.validationLogs = `Validation failed: ${error instanceof Error ? error.message : error}`;
      this.nzMessageService.error('File validation failed.');
    }
  }

  private async persistValidationResult(fileItem: FileItem, validationResult: ValidateFileResponse): Promise<void> {
    try {
      if (!fileItem.fileId) {
        const metadataCreated = (await this.uploadFilesService.createFileMetadata([fileItem])) as FileMetadata[] | void;
        fileItem.fileId = metadataCreated?.[0]?.fileId;
      }

      if (!fileItem.fileId) {
        throw new Error('missing file metadata');
      }

      const didSaveValidationResults = await this.uploadFilesService.updateValidationResults({
        orgId: this.userManagementService.getOrgId(),
        fileId: fileItem.fileId,
        validationResults: JSON.stringify(validationResult),
      });

      if (!didSaveValidationResults) {
        throw new Error('UpdateValidationResults returned success=false');
      }
    } catch (error) {
      console.warn(`Unable to persist validation results for ${fileItem.file?.name}.`, error);
    }
  }

  private async validateUploadedFile(fileItem: FileItem): Promise<ValidateFileResponse> {
    if (!fileItem.file) {
      throw new Error(`File not found for ${fileItem.nzFile?.name || 'uploaded file'}. Please re-upload the file.`);
    }

    const s3Key = fileItem.s3Key ?? (await this.uploadFileAndStoreS3Key(fileItem));
    const validationResult = await this.uploadFilesService.validateFile({
      orgId: this.userManagementService.getOrgId(),
      s3Key,
    });

    if (!validationResult) {
      this.filesValidated = false;
      throw new Error(`No validation response returned for ${fileItem.nzFile?.name || 'uploaded file'}.`);
    }

    return validationResult;
  }

  private async uploadFileAndStoreS3Key(
    fileItem: FileItem,
    onProgress?: (progress: { transferredBytes: number; totalBytes: number }) => void,
  ): Promise<string> {
    if (!(fileItem.file instanceof File)) {
      throw new Error('Invalid file type');
    }

    const s3Key = await this.uploadFilesService.uploadFileToS3(fileItem.file, onProgress);
    fileItem.s3Key = s3Key;

    return s3Key;
  }

  createRow(field: any): FormGroup {
    return this.fb.group({
      key: [field.key],
      file: [null, Validators.required],
      column: [null, Validators.required],
    });
  }

  pre(): void {
    if (this.index === 'fieldMapping') {
      this.resetMappings();
    }

    this.current -= 1;
    this.changeContent();
  }

  async next(): Promise<void> {
    // Step 1: uploads the files to S3
    await this.handleFileUpload();

    this.current += 1;
    this.changeContent();
  }

  changeContent(): void {
    switch (this.current) {
      case 0: {
        this.index = 'uploadFile';
        break;
      }
      case 1: {
        this.index = 'fieldMapping';
        break;
      }
      default: {
        this.index = 'error';
      }
    }
  }

  clearFiles(): void {
    this.validationLogs = '';
    this.filesValidated = false;
    this.uploadFilesService.resetFileProcessingState();
    this.uploadFilesService.isFileUploadedSubject.next(false);

    this.files[0]?.fileForm?.reset();
    this.files[0].headers = [];
    this.files[0].uploadedFileList = [];
    this.refreshFileOptions();
  }

  getBeforeUploadHandler(index: number) {
    return createCsvUploadBeforeUploadHandler(this.nzMessageService, (file, rawFile) => {
      this.uploadFilesService.resetFileProcessingState();
      this.files[index].nzFile = file;
      this.files[index].file = rawFile;
      this.files[index].s3Key = undefined;
      this.validationLogs = '';
      this.filesValidated = false;

      this.files[index].uploadedFileList = [file];

      this.refreshFileOptions();

      getFileMetadata(rawFile, this.nzMessageService, (headers, count) => {
        this.files[index].headers = headers;
        this.files[index].recordCount = count;
        this.refreshColumnOptionsByFile();
      });
    });
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
    this.validationLogs = '';
    this.uploadFilesService.resetFileProcessingState();
    if (this.files.length == 0) {
      this.addFile();
    }

    this.refreshFileOptions();
  }

  private refreshFileOptions(): void {
    this.fileOptions = this.files.flatMap((file) => file.uploadedFileList).map((file) => file.name);
    this.refreshColumnOptionsByFile();
    this.syncSingleFileForMappings();
  }

  private refreshColumnOptionsByFile(): void {
    this.columnOptionsByFile = this.files.reduce<Record<string, string[]>>((optionsByFile, fileItem) => {
      fileItem.uploadedFileList.forEach((file) => {
        optionsByFile[file.name] = fileItem.headers;
      });

      return optionsByFile;
    }, {});
  }

  private syncSingleFileForMappings(): void {
    if (!this.form) {
      return;
    }

    const selectedFile = this.fileOptions.length === 1 ? this.fileOptions[0] : null;

    this.mappings.controls.forEach((mapping) => {
      mapping.get('file')?.setValue(selectedFile);
    });
  }

  private resetMappings(): void {
    const selectedFile = this.fileOptions.length === 1 ? this.fileOptions[0] : null;

    this.mappings.controls.forEach((mapping) => {
      mapping.patchValue({
        file: selectedFile,
        column: null,
      });
      mapping.markAsPristine();
      mapping.markAsUntouched();
    });

    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.validationLogs = '';
    this.filesValidated = false;
  }

  async handleFileUpload(): Promise<void> {
    this.spinning = true;

    try {
      for (const fileItem of this.files) {
        await this.uploadFileAndStoreS3Key(fileItem, (progress) => {
          this.progressRatio = Math.round((progress.transferredBytes / progress.totalBytes) * 100);
        });
      }
      // Mark file uploaded
      this.uploadFilesService.isFileUploadedSubject.next(true);
      this.nzMessageService.success('File(s) upload successfully.');
    } catch (error: any) {
      this.nzMessageService.error(`File upload failed: ${error.message || error} `);
    } finally {
      this.spinning = false;
      this.progressRatio = 0;
    }
  }

  async saveMetadataAndProcess() {
    try {
      this.isProcessing = true;
      this.uploadFilesService.resetFileProcessingState();

      // Step 1: Save Metadata of the file
      const filesWithoutMetadata = this.files.filter((file) => !file.fileId);
      const metadataCreated = filesWithoutMetadata.length
        ? ((await this.uploadFilesService.createFileMetadata(filesWithoutMetadata)) as FileMetadata[])
        : [];

      this.files.forEach((file) => {
        file.fileId = file.fileId || metadataCreated?.find((item) => item.s3Key == file.s3Key)?.fileId;
        file.uploadStatus = UploadStatus.PROCESSING;
      });

      // Update Processing status
      const metadataUpdated = await this.uploadFilesService.updateFileMetadata(this.files);

      //Step 2: Add call for process api ( Encrypt and Hash)
      const processResponse = (await this.uploadFilesService.processFile(this.files)) as ProcessResponse[];

      if (
        this.files.every((item) => item.fileId) &&
        processResponse.length &&
        processResponse.every((item) => item.success)
      ) {
        this.nzMessageService.info('Successfully started the file processing.');
      } else {
        this.nzMessageService.error('An error occurred while processing the file. Please validate the uploaded file');
      }
    } catch (error) {
      console.error(error);
      this.nzMessageService.error('File processing failed, please try again. ');
    } finally {
      this.isProcessing = false;
    }
  }

  async export() {
    try {
      this.isPublishing = true;

      // Step 1: Save Metadata of the file
      const publishResponse = (await this.uploadFilesService.publishFile(this.files)) as PublishResponse[];

      this.files.forEach((file) => {
        publishResponse?.forEach((item) => {
          if (item.success === true) {
            file.exportStatus = ExportStatus.COMPLETED;
          } else {
            file.exportStatus = ExportStatus.FAILED;
          }
        });
      });

      //Step 2: Add call for process api ( Encrypt and Hash)
      const fileProcessed = await this.uploadFilesService.updateFileMetadata(this.files);

      if (publishResponse.length && publishResponse.every((item) => item.success)) {
        this.nzMessageService.success('Successfully published the processed file');
        this.router.navigate(['/files/history']);
        this.uploadFilesService.isFileUploadedSubject.next(false);
      } else {
        this.nzMessageService.error('An error occurred while publishing file. Please try again');
      }
    } catch (error) {
      console.error(error);
      this.nzMessageService.error('An error occurred while publishing file. Please try again');
    } finally {
      this.isPublishing = false;
    }
  }

  updateItemInList(updatedItem: FileMetadata): void {
    this.files = this.files.map((item) => {
      if (item.fileId === updatedItem.fileId) {
        if (updatedItem.uploadStatus && item.uploadStatus !== updatedItem.uploadStatus) {
          this.uploadFilesService.announceStateUpdate(updatedItem);
        }
        return updatedItem;
      }
      return item;
    }) as FileItem[];
  }
}
